import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { Loader } from '../../../components/common/Loader';
import { PreviewDetails } from '../../../components/preview-details/PreviewDetails';
import { getFragment } from '../../../graphql';
import {
    GetProviderOffersDocument,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import { getAbsoluteRoute } from '../../../util';

const PreviewDetailsRoute = () => {
    const { providerOffersId, offerId, prescriptionId } = useParams();

    const { data, loading } = useQuery(GetProviderOffersDocument, {
        variables: { id: providerOffersId ?? '' },
        skip: !providerOffersId || !prescriptionId,
    });

    const providerOffer = getFragment(data?.providerOffers);
    const selectedOffer = getFragment(
        providerOffer?.offers.find((x) => getFragment(x).id === offerId)
    );

    const prescriberOrder = getFragment(providerOffer?.prescriberOrder);
    const { isApprovedPriorAuthorization, priorAuthorizationDetails } =
        usePriorAuthorizationDetails(prescriberOrder);

    const redirectToPriorAuthPage = useMemo(() => {
        return (
            !priorAuthorizationDetails?.isSpecialty &&
            isApprovedPriorAuthorization &&
            prescriberOrder?.status !== PrescriberOrderStatus.Pending
        );
    }, [
        priorAuthorizationDetails,
        isApprovedPriorAuthorization,
        prescriberOrder?.status,
    ]);

    if (loading) {
        return <Loader />;
    }

    if (!selectedOffer || !prescriberOrder) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (priorAuthorizationDetails && redirectToPriorAuthPage) {
        return (
            <Navigate
                to={getAbsoluteRoute(
                    'priorAuthorization.priorAuthorizationId.status',
                    {
                        priorAuthorizationId: priorAuthorizationDetails.id,
                    }
                )}
            />
        );
    }

    return (
        <PreviewDetails
            offer={selectedOffer}
            prescriberOrder={prescriberOrder}
        />
    );
};

export default PreviewDetailsRoute;
